import React from 'react';

import { postWebUiApi } from '../../api';
import { WebUiAlert } from '../../components/alert';
import { WebUiButton } from '../../components/button';
import { WebUiInput } from '../../components/input';
import WebUiPageWrapper from '../../components/page-wrapper';
import { WebUiSelect } from '../../components/select';
import { useWebUiGlobalContext } from '../../context/global/use-global-context';

type NtpConfig = { enable?: boolean; server?: string; queryInterval?: number };

export default function WebUiSettingsNtpPage() {
  const { currentUser } = useWebUiGlobalContext();
  const readonly = currentUser?.userLevel === 1;
  const [cfg, setCfg] = React.useState<NtpConfig>({});
  const [time, setTime] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const [message, setMessage] = React.useState<string | null>(null);

  const reload = React.useCallback(async () => {
    const [config, t] = await Promise.all([
      postWebUiApi<NtpConfig>({
        service: 'ntp',
        access: 'get',
        command: 'get config',
      }),
      postWebUiApi<{ time?: string }>({
        service: 'system',
        access: 'get',
        command: 'get time',
      }),
    ]);
    setCfg(config);
    setTime(t.time ?? '');
  }, []);

  React.useEffect(() => {
    reload().catch((err) => setError((err as Error).message));
  }, [reload]);

  return (
    <WebUiPageWrapper titleList={['Settings', 'NTP Client']}>
      {message ? <WebUiAlert type="success" message={message} /> : null}
      {error ? <WebUiAlert type="error" message={error} /> : null}
      <div className="flex w-full flex-row gap-2">
        <div className="flex w-1/2 flex-col gap-2">
          <table className="webui-table w-full">
            <thead>
              <tr className="webui-table-row-head">
                <td>Option</td>
                <td>Setting</td>
              </tr>
            </thead>
            <tbody>
              <tr className="webui-table-row-content">
                <td>Enable NTP Client</td>
                <td>
                  <input
                    type="checkbox"
                    disabled={readonly}
                    checked={Boolean(cfg.enable)}
                    onChange={(e) =>
                      setCfg((p) => ({ ...p, enable: e.target.checked }))
                    }
                  />
                </td>
              </tr>
              <tr className="webui-table-row-content">
                <td>NTP Server</td>
                <td>
                  <WebUiInput
                    inputSize="sm"
                    className="w-full"
                    disabled={readonly}
                    value={cfg.server ?? ''}
                    onChange={(e) =>
                      setCfg((p) => ({ ...p, server: e.target.value }))
                    }
                  />
                </td>
              </tr>
              <tr className="webui-table-row-content">
                <td>Query Interval</td>
                <td>
                  <WebUiSelect
                    inputSize="sm"
                    className="w-full"
                    options={[
                      { label: '8 seconds', value: '3' },
                      { label: '16 seconds', value: '4' },
                      { label: '32 seconds', value: '5' },
                      { label: '1 minute', value: '6' },
                      { label: '2 minutes', value: '7' },
                      { label: '4 minutes', value: '8' },
                      { label: '8 minutes', value: '9' },
                      { label: '17 minutes', value: '10' },
                      { label: '34 minutes', value: '11' },
                      { label: '68 minutes', value: '12' },
                      { label: '136 minutes', value: '13' },
                      { label: '273 minutes', value: '14' },
                      { label: '546 minutes', value: '15' },
                      { label: '1092 minutes', value: '16' },
                    ]}
                    disabled={readonly}
                    value={String(cfg.queryInterval ?? 6)}
                    onChange={(e) =>
                      setCfg((p) => ({
                        ...p,
                        queryInterval: Number(e.target.value),
                      }))
                    }
                  />
                </td>
              </tr>
            </tbody>
          </table>
          <div>
            <WebUiButton
              disabled={readonly}
              onClick={async () => {
                try {
                  await postWebUiApi({
                    service: 'ntp',
                    access: 'set',
                    command: 'set config',
                    parameters: cfg,
                  });
                } catch (err) {
                  setError((err as Error).message);
                }
              }}
            >
              Set
            </WebUiButton>
          </div>
        </div>
        <div className="flex w-1/2 flex-col gap-2">
          <table className="webui-table w-full">
            <thead>
              <tr className="webui-table-row-head">
                <td>Option</td>
                <td>Value</td>
              </tr>
            </thead>
            <tbody>
              <tr className="webui-table-row-content">
                <td>Time</td>
                <td>{time || '-'}</td>
              </tr>
            </tbody>
          </table>
          <div>
            <WebUiButton
              onClick={async () => {
                try {
                  await postWebUiApi({
                    service: 'system',
                    access: 'set',
                    command: 'set time',
                  });
                  setMessage('Time queried.');
                  setError(null);
                  await reload();
                } catch (err) {
                  setError((err as Error).message);
                }
              }}
            >
              Query Time
            </WebUiButton>
          </div>
        </div>
      </div>
    </WebUiPageWrapper>
  );
}
